import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Loader from '../components/Loader';
import useLoading from '../hooks/useLoading';
import useImagePreload from '../hooks/useImagePreload';
import {
    LightBulbIcon,
    StarIcon,
    SparklesIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    TrophyIcon,
    UserIcon,
    ClockIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const About = () => {
    const isLoading = useLoading(1500);
    const imagesLoaded = useImagePreload([
        'https://images.unsplash.com/photo-1497366216548-37526070297c',
        'https://images.unsplash.com/photo-1500648767791-00dcc994a43e',
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
        'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e',
    ]);

    const team = [
        {
            name: 'Parth Sharma',
            role: 'Principal Designer & Founder',
            image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e',
            bio: 'With over 15 years of experience in interior design, Parth brings a unique blend of creativity and functionality to every project.',
        },
        {
            name: 'Sarah Johnson',
            role: 'Senior Interior Designer',
            image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
            bio: "Specializing in residential design, Sarah creates spaces that reflect each client's personality and lifestyle.",
        },
        {
            name: 'Michael Chen',
            role: 'Commercial Design Specialist',
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e',
            bio: 'Michael excels in creating innovative commercial spaces that enhance brand identity and user experience.',
        },
    ];

    const stats = [
        { number: '150+', label: 'Projects Completed', icon: BuildingOfficeIcon },
        { number: '12+', label: 'Years Experience', icon: ClockIcon },
        { number: '50+', label: 'Happy Clients', icon: UserIcon },
        { number: '15', label: 'Design Awards', icon: TrophyIcon },
    ];

    const values = [
        {
            title: 'Innovation',
            description: 'Pushing boundaries with creative and forward-thinking design solutions.',
            icon: LightBulbIcon,
        },
        {
            title: 'Quality',
            description: 'Commitment to excellence in every detail of our design process.',
            icon: StarIcon,
        },
        {
            title: 'Sustainability',
            description: 'Incorporating eco-friendly practices and materials in our designs.',
            icon: SparklesIcon,
        },
        {
            title: 'Client Focus',
            description: "Putting our clients' needs and vision at the center of every project.",
            icon: UserGroupIcon,
        },
    ];

    if (isLoading || !imagesLoaded) {
        return <Loader />;
    }

    return (
        <div className="min-h-screen bg-dark">
            {/* Hero Section */}
            <section className="relative py-24 bg-primary text-light">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/70 to-primary/90" />
                <div className="container mx-auto px-4 relative">
                    <motion.div
                        className="text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6">About Us</h1>
                        <p className="text-xl text-light/90 max-w-2xl mx-auto">
                            Creating exceptional spaces that inspire and transform lives through innovative design
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Story Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="space-y-6"
                        >
                            <h2 className="font-serif text-4xl font-bold text-light mb-6">Our Story</h2>
                            <div className="space-y-4 text-light/80">
                                <p>
                                    Founded in 2010, Parth Interiors has grown from a small design studio to a leading interior design firm, known for creating exceptional spaces that combine aesthetics with functionality.
                                </p>
                                <p>
                                    Our journey began with a simple vision: to transform spaces into inspiring environments that enhance the way people live, work, and interact.
                                </p>
                                <p>
                                    Today, we continue to push the boundaries of design, bringing innovation and creativity to every project while maintaining our commitment to quality and client satisfaction.
                                </p>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative h-96 rounded-2xl overflow-hidden shadow-2xl group"
                        >
                            <motion.img
                                src="https://images.unsplash.com/photo-1497366216548-37526070297c"
                                alt="Our Office"
                                className="w-full h-full object-cover"
                                whileHover={{ scale: 1.1 }}
                                transition={{ duration: 0.7 }}
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-primary text-light">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                className="text-center group"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                            >
                                <motion.div
                                    whileHover={{ scale: 1.1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <stat.icon className="w-12 h-12 mx-auto mb-4 text-accent" />
                                </motion.div>
                                <div className="font-serif text-4xl font-bold text-accent mb-2">{stat.number}</div>
                                <div className="text-light/90">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-24">
                <div className="container mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-serif text-4xl font-bold text-light mb-6">Our Values</h2>
                        <p className="text-xl text-light/80 max-w-2xl mx-auto">
                            The principles that guide our work and relationships
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="card group hover:shadow-xl hover:bg-dark/70 transition-all duration-300"
                                whileHover={{ y: -5 }}
                            >
                                <motion.div
                                    whileHover={{ scale: 1.1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <value.icon className="w-12 h-12 text-accent mb-4" />
                                </motion.div>
                                <h3 className="font-serif text-xl font-bold text-light mb-3">{value.title}</h3>
                                <p className="text-light/80">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-24 bg-dark/50">
                <div className="container mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-serif text-4xl font-bold text-light mb-6">Meet Our Team</h2>
                        <p className="text-xl text-light/80 max-w-2xl mx-auto">
                            Talented professionals dedicated to bringing your vision to life
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {team.map((member, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                className="card group overflow-hidden hover:bg-dark/70 transition-all duration-300"
                                whileHover={{ y: -5 }}
                            >
                                <div className="h-80 relative overflow-hidden">
                                    <motion.img
                                        src={member.image}
                                        alt={member.name}
                                        className="w-full h-full object-cover"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ duration: 0.7 }}
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                </div>
                                <div className="p-6">
                                    <h3 className="font-serif text-2xl font-bold text-light mb-2">{member.name}</h3>
                                    <p className="text-accent mb-4">{member.role}</p>
                                    <p className="text-light/80">{member.bio}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-accent text-dark text-center">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-3xl mx-auto"
                    >
                        <h2 className="font-serif text-4xl font-bold mb-6">
                            Let's Create Something Beautiful Together
                        </h2>
                        <p className="text-xl mb-8 text-dark/80">
                            Start your journey towards a beautifully designed space
                        </p>
                        <Link
                            to="/contact"
                            className="btn-primary bg-dark text-light hover:bg-dark/90 group inline-flex items-center"
                        >
                            Get in Touch
                            <ArrowRightIcon className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default About; 